import { Tick } from '@/core/ticks';
import { TickProcessed } from './types';
import { MAX_TICK, MIN_TICK, feeTierToTickSpacing } from 'nftx.js';

export const PRICE_FIXED_DIGITS = 4;
export const DEFAULT_SURROUNDING_TICKS = 300;

type KeyByMapper<T> = string | ((item: T) => string);

function keyBy<T>(array: T[], mapper: KeyByMapper<T>): Record<string, T> {
  return array.reduce((result, item) => {
    const key =
      typeof mapper === 'string'
        ? (item as any)[mapper]
        : (mapper as (item: T) => string)(item);
    result[key] = item;
    return result;
  }, {} as Record<string, T>);
}

export const formatTicksForChart = (
  liquidity: bigint,
  activeTickIdx: number,
  feeTier: number,
  ticks: Tick[] | [],
  numSurroundingTicks: number,
  tickUpper: number,
  tickLower: number,
  userLiquidity: bigint | undefined
) => {
  if (!ticks.length) return [];

  const tickSpacing = feeTierToTickSpacing(feeTier);

  const tickIdxToInitializedTick = keyBy(ticks, 'index');

  // If the pool's tick is MIN_TICK, then when we find the closest
  // initializable tick to its left, the value would be smaller than MIN_TICK.
  // In this case we must ensure that the prices shown never go below/above.
  // what actual possible from the protocol.
  let activeTickIdxForPrice = Number(activeTickIdx);
  if (activeTickIdxForPrice < MIN_TICK) {
    activeTickIdxForPrice = MIN_TICK;
  }
  if (activeTickIdxForPrice > MAX_TICK) {
    activeTickIdxForPrice = MAX_TICK;
  }

  const activeTickProcessed: TickProcessed = {
    liquidityActive: BigInt(liquidity),
    liquidityUser:
      tickLower >= Number(activeTickIdx) &&
      tickUpper <= Number(activeTickIdx) &&
      userLiquidity != null
        ? userLiquidity
        : 0n,
    tickIdx: Number(activeTickIdx),
    liquidityNet: BigInt(0),
    price0: tickIdxToInitializedTick[activeTickIdx]?.prices[0].toString(),
    price1: tickIdxToInitializedTick[activeTickIdx]?.prices[1].toString(),
    liquidityGross: BigInt(0),
  };

  // If our active tick happens to be initialized (i.e. there is a position that starts or
  // ends at that tick), ensure we set the gross and net.
  // correctly.
  const activeTick = tickIdxToInitializedTick[activeTickIdx];
  if (activeTick) {
    activeTickProcessed.liquidityGross = BigInt(activeTick.liquidityGross);
    activeTickProcessed.liquidityNet = BigInt(activeTick.liquidityNet);
  }

  enum Direction {
    ASC,
    DESC,
  }

  // Computes the numSurroundingTicks above or below the active tick.
  const computeSurroundingTicks = (
    activeTickProcessed: TickProcessed,
    tickSpacing: number,
    numSurroundingTicks: number,
    direction: Direction
  ) => {
    let previousTickProcessed: TickProcessed = {
      ...activeTickProcessed,
    };

    // Iterate outwards (either up or down depending on 'Direction') from the active tick,
    // building active liquidity for every tick.
    let processedTicks: TickProcessed[] = [];
    for (let i = 0; i < numSurroundingTicks; i++) {
      const currentTickIdx =
        direction == Direction.ASC
          ? previousTickProcessed.tickIdx + tickSpacing
          : previousTickProcessed.tickIdx - tickSpacing;

      if (currentTickIdx < MIN_TICK || currentTickIdx > MAX_TICK) {
        break;
      }

      const liquidityUser =
        (tickLower <= currentTickIdx && tickUpper >= currentTickIdx
          ? userLiquidity
          : 0n) ?? 0n;

      const currentTickProcessed: TickProcessed = {
        liquidityActive: previousTickProcessed.liquidityActive,
        tickIdx: currentTickIdx,
        liquidityNet: 0n,
        price0: tickIdxToInitializedTick[currentTickIdx]?.prices[0].toString(),
        price1: tickIdxToInitializedTick[currentTickIdx]?.prices[1].toString(),
        liquidityGross: 0n,
        liquidityUser,
      };

      // Check if there is an initialized tick at our current tick.
      // If so copy the gross and net liquidity from the initialized tick.
      const currentInitializedTick =
        tickIdxToInitializedTick[currentTickIdx.toString()];
      if (currentInitializedTick) {
        currentTickProcessed.liquidityGross = BigInt(
          currentInitializedTick.liquidityGross
        );
        currentTickProcessed.liquidityNet = BigInt(
          currentInitializedTick.liquidityNet
        );
      }

      // Update the active liquidity.
      // If we are iterating ascending and we found an initialized tick we immediately apply
      // it to the current processed tick we are building.
      // If we are iterating descending, we don't want to apply the net liquidity until the following tick.
      if (direction == Direction.ASC && currentInitializedTick) {
        currentTickProcessed.liquidityActive =
          previousTickProcessed.liquidityActive +
          BigInt(currentInitializedTick.liquidityNet);
      } else if (
        direction == Direction.DESC &&
        previousTickProcessed.liquidityNet != 0n
      ) {
        // We are iterating descending, so look at the previous tick and apply any net liquidity.
        currentTickProcessed.liquidityActive =
          previousTickProcessed.liquidityActive -
          previousTickProcessed.liquidityNet;
      }

      processedTicks.push(currentTickProcessed);
      previousTickProcessed = currentTickProcessed;
    }

    if (direction == Direction.DESC) {
      processedTicks = processedTicks.reverse();
    }

    return processedTicks;
  };

  const subsequentTicks: TickProcessed[] = computeSurroundingTicks(
    activeTickProcessed,
    tickSpacing,
    numSurroundingTicks,
    Direction.ASC
  );

  const previousTicks: TickProcessed[] = computeSurroundingTicks(
    activeTickProcessed,
    tickSpacing,
    numSurroundingTicks,
    Direction.DESC
  );

  const ticksProcessed = previousTicks
    .concat(activeTickProcessed)
    .concat(subsequentTicks);

  return ticksProcessed.map((x) => ({
    ...x,
    activeLiquidity: parseFloat(x.liquidityActive.toString()),
    userLiquidity: parseFloat(x.liquidityUser.toString()),
  }));
};
